import { createInstance, UserAttributes, Client, OptimizelyUserContext } from '@optimizely/optimizely-sdk';
import { enums } from '@optimizely/react-sdk';
import Config from '../Config';
import CookieConsentStore from '../stores/CookieConsentStore';
import GaDataStore from '../stores/GaDataStore';
import CookieApi from './CookieApi';

export const OPTIMIZELY_SDK_KEY = Config.optimizelySdkKey;
export const VARIATION_OFF = 'Off';
export const TEST_DISABLED = 'disabled';
export const TEST_PENDING = 'pending';

interface DecisionInfo {
  decisionEventDispatched?: boolean;
  enabled?: boolean;
  flagKey?: string;
  ruleKey?: string;
  variationKey?: string;
}

interface OnDecisionProps {
  type: string;
  attributes: UserAttributes;
  userId: string;
  decisionInfo: DecisionInfo;
}

export const onDecision = ({ type, decisionInfo }: OnDecisionProps) => {
  if (type === 'flag') {
    const variationKey = decisionInfo.enabled && decisionInfo.variationKey ? decisionInfo.variationKey : TEST_DISABLED;
    GaDataStore.pushOptimizelyEvent(decisionInfo.flagKey, variationKey);
  }
};

const processOptimizelyDecision = (
  optimizely: Client,
  user: OptimizelyUserContext,
  featureKey: string,
  variations: { [key: string]: string },
  callback: (variation: string) => void,
) => {
  const notificationId = optimizely.notificationCenter.addNotificationListener(enums.NOTIFICATION_TYPES.DECISION, onDecision);

  const { variationKey, enabled } = user.decide(featureKey);

  optimizely.notificationCenter.removeNotificationListener(notificationId);

  const variation = enabled && variationKey ? variations[variationKey] : VARIATION_OFF;

  callback(variation);
};

export const abTestDecider = (
  featureKey: string,
  variations: {
    [key: string]: string;
  },
  callback: (variation: string) => void,
): string => {
  const statisticsConsent = CookieConsentStore.isStatisticsCookiesEnabled();
  if (!statisticsConsent) {
    return VARIATION_OFF;
  }

  const optimizely = createInstance({ sdkKey: OPTIMIZELY_SDK_KEY });
  if (!optimizely) {
    GaDataStore.pushOptimizelyEvent(featureKey, TEST_DISABLED);
    return VARIATION_OFF;
  }

  optimizely.onReady().then(({ success }) => {
    if (!success) {
      GaDataStore.pushOptimizelyEvent(featureKey, TEST_DISABLED);
      callback(VARIATION_OFF);
      return;
    }

    const user = optimizely.createUserContext(CookieApi.getCookieValue('_ga'));

    if (!user) {
      GaDataStore.pushOptimizelyEvent(featureKey, TEST_DISABLED);
      callback(VARIATION_OFF);
      return;
    }

    processOptimizelyDecision(optimizely, user, featureKey, variations, callback);
  });

  return TEST_PENDING;
};
